// Phase 1A Step 37 — Unified Shell styles.
//
// Token-only CSSProperties for the additive unified institutional
// shell. No hex literals, no icon library, no emoji, no drop shadows.
// Pattern matches src/modes/mode-shell.styles.ts (the existing
// production convention): per-folder `.styles.ts` companion exporting
// `CSSProperties` objects, consumed via inline `style={...}` on the
// component. No global CSS additions in Step 37.
//
// Source: handoff_phantom_mode/prototype/shell.jsx — adapted to use
// existing --sz-* tokens (incl. Step 36's --sz-strip-h / --sz-rail-w).

import type { CSSProperties } from "react";

// ─── Outer wrapper ────────────────────────────────────────────────

export const shell: CSSProperties = {
  minHeight: "var(--vh-full)",
  display: "flex",
  flexDirection: "column",
  background: "var(--sz-bg-base)",
  color: "var(--sz-text-primary)",
};

// ─── Context strip (36px sticky header) ───────────────────────────

const stripBase: CSSProperties = {
  height: "var(--sz-strip-h)",
  borderBottom: "1px solid var(--sz-border-subtle)",
  background: "var(--sz-bg-surface)",
  display: "flex",
  alignItems: "center",
  padding: "0 var(--sz-space-4) 0 var(--sz-space-5)",
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "1.5px",
  color: "var(--sz-text-muted)",
  position: "sticky",
  top: 0,
  zIndex: 30,
  transition: "opacity var(--sz-duration-standard) var(--sz-ease-standard)",
};

export const strip: CSSProperties = { ...stripBase, opacity: 1 };
export const stripDimmed: CSSProperties = { ...stripBase, opacity: 0.5 };

// Vertical separator inside the context strip.
export const stripSep: CSSProperties = {
  width: 1,
  height: 14,
  background: "var(--sz-border-default)",
  margin: "0 var(--sz-space-3)",
};

export const stripWordmark: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--sz-space-2)",
  color: "var(--sz-amber-text)",
  fontFamily: "var(--sz-font-display)",
  fontSize: "13px",
  letterSpacing: "3px",
  lineHeight: 1,
};

export const stripContextLabel: CSSProperties = {
  color: "var(--sz-text-secondary)",
};

export const stripChainLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  color: "var(--sz-text-muted)",
  letterSpacing: "1.5px",
};

export const stripChainValue: CSSProperties = {
  marginLeft: "var(--sz-space-1)",
  color: "var(--sz-amber-text)",
  fontWeight: 500,
  fontVariantNumeric: "tabular-nums",
};

export const stripSafety: CSSProperties = {
  color: "var(--sz-success-text)",
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--sz-space-2)",
};

// Live dot used in the safety indicator. Ported inline (not added to
// globals.css) so the shell stays self-contained in Step 37.
export const stripSafetyDot: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "var(--sz-radius-full)",
  background: "var(--sz-success-text)",
  display: "inline-block",
};

export const stripSpacer: CSSProperties = {
  marginLeft: "auto",
};

// Account dot — placeholder per UI-architecture §2.3 (Phase 1
// non-functional, tooltip-only). Mirrors src/modes/mode-shell.styles.ts
// `accountDot` but sized for the 36px strip (24px diameter).
export const stripAccountDot: CSSProperties = {
  width: 24,
  height: 24,
  borderRadius: "var(--sz-radius-full)",
  background: "var(--sz-bg-surface-2)",
  border: "1px solid var(--sz-border-strong)",
  color: "var(--sz-text-muted)",
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "default",
};

// ─── Body row: main + right rail ──────────────────────────────────

export const body: CSSProperties = {
  display: "flex",
  flex: 1,
  minHeight: 0,
};

export const main: CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: "var(--sz-bg-base)",
  position: "relative",
};

// ─── Right rail (56px) ────────────────────────────────────────────

export const rail: CSSProperties = {
  width: "var(--sz-rail-w)",
  borderLeft: "1px solid var(--sz-border-subtle)",
  background: "var(--sz-bg-surface)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "var(--sz-space-5) 0",
  gap: "var(--sz-space-1)",
  position: "sticky",
  top: "var(--sz-strip-h)",
  alignSelf: "flex-start",
  height: "calc(var(--vh-full) - var(--sz-strip-h))",
};

const railItemBase: CSSProperties = {
  width: "calc(var(--sz-rail-w) - 8px)",
  height: 44,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: 0,
  borderLeft: "2px solid transparent",
  background: "transparent",
  transition:
    "color var(--sz-duration-fast) var(--sz-ease-standard)," +
    " background var(--sz-duration-fast) var(--sz-ease-standard)",
  position: "relative",
};

export const railItem: CSSProperties = {
  ...railItemBase,
  color: "var(--sz-text-muted)",
};

export const railItemActive: CSSProperties = {
  ...railItemBase,
  color: "var(--sz-amber-text)",
  borderLeft: "2px solid var(--sz-amber)",
  background: "var(--sz-bg-surface-2)",
};

// Tooltip rendered to the left of a hovered rail button.
export const railTooltip: CSSProperties = {
  position: "absolute",
  right: "calc(var(--sz-rail-w) + 4px)",
  top: "50%",
  transform: "translateY(-50%)",
  whiteSpace: "nowrap",
  background: "var(--sz-bg-elevated)",
  border: "1px solid var(--sz-border-default)",
  borderRadius: "var(--sz-radius-md)",
  padding: "6px 10px",
  fontFamily: "var(--sz-font-mono)",
  fontSize: "10px",
  letterSpacing: "1.5px",
  color: "var(--sz-text-primary)",
  zIndex: 40,
  pointerEvents: "none",
};

export const railTooltipSymbol: CSSProperties = {
  marginLeft: 6,
  color: "var(--sz-text-muted)",
};

// Bottom-anchored version stamp (e.g. "v 0.1 · 1A") in vertical
// orientation, matching the prototype rail's footer.
export const railVersionWrap: CSSProperties = {
  marginTop: "auto",
  textAlign: "center",
  padding: "var(--sz-space-2)",
};

export const railVersionText: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "8px",
  letterSpacing: "3px",
  color: "var(--sz-text-disabled)",
  writingMode: "vertical-rl",
  textOrientation: "mixed",
};
